import React, { useEffect, useState } from "react";

/**
 * SplashFlash — the "Holy Words presents" brand pre-roll.
 * ======================================================
 * Restored Jun 2026 per owner request. Shown EVERY time a player opens
 * (video / sermon / gospel / movie / cartoon / FBR — anything with a
 * mini or full player). The Holy Words gold logo fades in over a solid
 * terracotta-red background that covers the stage from the very first
 * frame, then the whole splash fades away to reveal the media.
 *
 * IMPORTANT — by design the splash and the media start at the SAME time:
 * the video begins playing (muted-then-unmuted, exactly as before) BEHIND
 * this opaque overlay. That's intentional — it hides the YouTube/source
 * intro + logo and promotes Holy Words branding for the first ~7 seconds.
 * Audio coming up under the splash is expected, not a bug.
 *
 * Props:
 *   itemId  – stable id of the currently-playing item. Changing it
 *             re-fires the splash, so a new title triggers it but a
 *             pause/resume of the same one does not.
 *   onDone  – optional callback fired once the splash finishes (7s).
 *   tagline – big text under the logo. Defaults to "Holy Words".
 *   sub     – small secondary line (usually the title). Optional.
 *
 * Fully `pointer-events: none` so it never blocks the player controls
 * underneath during its lifetime.
 */

// Per-theme splash backdrop. The owner asked the brand pre-roll to match
// the active color theme: BLUE in Midnight, PURPLE in Amethyst, BLACK in
// OLED/dark mode. Every other theme keeps the brand deep-red so the gold
// logo + amber wordmark always read with strong contrast. The theme is
// read from <html data-theme="…">, which index.html sets before React
// even mounts, so it's always available.
const THEME_SPLASH_BG = {
  oled: "#000000",      // Dark mode → black
  midnight: "#1E3A8A",  // Blue mode → deep blue
  amethyst: "#3D1A78",  // Purple mode → rich purple
  crimson: "#9E2317",   // Red theme → brand red
  sunrise: "#9E2317",   // Light theme → brand red (keeps logo contrast)
  sanctuary: "#9E2317", // Light theme → brand red (keeps logo contrast)
};
function splashBgForTheme() {
  try {
    return THEME_SPLASH_BG[document.documentElement.dataset.theme] || "#9E2317";
  } catch (_) {
    return "#9E2317";
  }
}

export default function SplashFlash({ itemId, onDone, tagline = "Holy Words", sub = "" }) {
  const [phase, setPhase] = useState("enter"); // "enter" | "hold" | "exit" | "done"

  useEffect(() => {
    setPhase("enter");
    // ~7s total: hold the brand moment, then fade into the video.
    const t2 = setTimeout(() => setPhase("hold"), 260);
    const t3 = setTimeout(() => setPhase("exit"), 6500);
    const t4 = setTimeout(() => {
      setPhase("done");
      try { onDone && onDone(); } catch (_) { /* noop */ }
    }, 7000);
    return () => {
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
    // Re-fire whenever the playing item changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  if (phase === "done") return null;

  const exiting = phase === "exit";
  // Backdrop color follows the active theme (blue / purple / black / red).
  // Read on each render so a fresh splash always matches the current theme.
  const bg = splashBgForTheme();

  return (
    <div
      data-testid="splash-flash"
      aria-hidden="true"
      // The themed backdrop is OPAQUE from the very first frame, so it covers
      // the player's black background instantly — you never see black before
      // the brand appears. The logo + wordmark are shown SOLID (no entrance
      // animation, per owner request — the scale/slide-in read as a "jump").
      // On `exit` the whole backdrop (logo included, as its child) fades OUT
      // over 500ms, dissolving into the now-playing video. Backdrop color is
      // theme-driven: blue in Midnight, purple in Amethyst, black in OLED,
      // brand red everywhere else.
      style={{ backgroundColor: bg }}
      className={`absolute inset-0 z-30 pointer-events-none grid place-items-center overflow-hidden transition-opacity duration-500 ease-out ${
        exiting ? "opacity-0" : "opacity-100"
      }`}
    >
      {/* Theme-neutral studio-bumper vignette: a soft light glow at the
          center fading to a darkened edge. Works on ANY backdrop color
          (red / blue / purple / black) so the gold logo always pops. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% 42%, rgba(255,255,255,0.13), rgba(0,0,0,0) 55%, rgba(0,0,0,0.45) 100%)",
        }}
      />
      <div className="relative flex flex-col items-center gap-4 select-none">
        {/* Gold Holy Words logo. */}
        <img
          src="/icons/icon-512.png?v=3"
          alt=""
          width={144}
          height={144}
          loading="eager"
          decoding="async"
          className="w-28 h-28 sm:w-36 sm:h-36 rounded-[22%] shadow-2xl ring-1 ring-amber-200/40"
        />
        <div
          className="text-amber-200 font-heading text-2xl sm:text-3xl tracking-[0.22em] uppercase font-bold"
          style={{ textShadow: "0 2px 14px rgba(0,0,0,0.4)" }}
        >
          {tagline}
        </div>
        {sub && (
          <div className="text-cream/85 text-[10px] sm:text-xs tracking-[0.32em] uppercase max-w-[80vw] truncate">
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}
